import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/** Thin global ticker rendered just below the Navbar on every page.
 *  Continuous CSS marquee — pauses on hover, seamlessly loops the product list.
 *  Uses only real Samara Wiz SKUs (`SW-*`) via the /products endpoint.
 *  SP is shown ONLY when: user is logged in AND we are NOT on the Home page
 *  (per master requirements: no SP on home page).
 */
export default function ProductTicker() {
  const [items, setItems] = useState([]);
  const trackRef = useRef(null);
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const showSp = !!user && !isHome;
  // HP-02 of the functional requirements — no MRP or AP on Home banners/sliders.
  // Only show prices on inner pages when the user is logged in (as their AP/DP).
  const showPrice = !!user && !isHome;

  useEffect(() => {
    const load = () => api.get("/products?limit=24").then((r) => {
      const list = (r.data.items || []).filter((p) => (p.sku || "").startsWith("SW-"));
      setItems(list);
    }).catch(() => {});
    load();
    // Auto-refresh so customers see catalog updates without a manual reload:
    //   • every 60s while the tab is open
    //   • immediately when the tab regains focus after being backgrounded
    const iv = setInterval(load, 60000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, []);

  if (!items.length) return null;

  return (
    <div data-testid="product-ticker" className="bg-royal border-y border-royal-deep overflow-hidden">
      <div className="max-w-full mx-auto flex items-stretch">
        <div className="shrink-0 hidden sm:flex items-center gap-2 bg-gold text-royal px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-medium">
          <Sparkles size={12} /> Live Shop
        </div>
        <div ref={trackRef} className="marquee-track flex-1 overflow-hidden group">
          <div className="animate-marquee-fast flex gap-8 py-1.5 w-max group-hover:[animation-play-state:paused]">
            {[...items, ...items].map((p, i) => (
              <Link
                key={`${p.id}-${i}`}
                to={user ? `/product/${p.id}` : "/login"}
                title={user ? p.name : "Login / register to view product details"}
                data-testid={`ticker-item-${p.sku}`}
                className="inline-flex items-center gap-2 text-[12px] whitespace-nowrap text-white/90 hover:text-gold transition-colors"
              >
                <span className="w-1 h-1 bg-gold rounded-full" />
                <span className="font-medium tracking-wide">{p.name}</span>
                {showPrice && (
                  <span className="text-gold font-semibold">{inr(p.dp)}</span>
                )}
                {showSp && (
                  <span className="text-white/40 text-[10px] uppercase tracking-widest">
                    · {Number.isInteger(p.sp) ? p.sp : Number(p.sp).toFixed(2)} SP
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
